import { EGRESS_AUDIENCES, egressLoosenedAudiences, egressPolicySchema } from "@esi/shared";
import { t as translate } from "../i18n/i18n.ts";

/**
 * `tools.egress` in words, one wording for the console, the backoffice and projects (openspec
 * change settings-typed-controls, D10). Plain functions, no React.
 */

export type EgressAudience = (typeof EGRESS_AUDIENCES)[number];
export type { EgressAction } from "@esi/shared";

/** An audience's name: the readers' own words (`readers.*`), "Named readers" for the last. */
export function egressAudienceName(audience: EgressAudience): string {
  return audience === "named"
    ? translate("settingsControls.egress.named")
    : translate(`readers.${audience}`);
}

/** The policy in words ("Project audience: allowed · …"); null when it does not parse. */
export function describeEgress(value: unknown): string | null {
  const parsed = egressPolicySchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }
  const policy = parsed.data;
  return EGRESS_AUDIENCES.map(
    (a) => `${egressAudienceName(a)}: ${translate(`settingsControls.egress.${policy[a]}`)}`,
  ).join(" · ");
}

/** The audiences `to` loosens from `from`, with both actions; null when a value does not parse. */
function loosenedMoves(from: unknown, to: unknown) {
  const a = egressPolicySchema.safeParse(from);
  const b = egressPolicySchema.safeParse(to);
  if (!a.success || !b.success) {
    return null;
  }
  return egressLoosenedAudiences(a.data, b.data).map((audience) => ({
    audience,
    from: a.data[audience],
    to: b.data[audience],
  }));
}

/**
 * The effect of a loosening, one sentence per audience moved toward Allowed ("After a
 * conversation reads files only named people may read, web search and web fetch will ask first
 * instead of being blocked."); null when nothing loosens or a value does not parse.
 */
export function egressLoosenSentence(from: unknown, to: unknown): string | null {
  const moves = loosenedMoves(from, to);
  if (!moves || moves.length === 0) {
    return null;
  }
  return moves
    .map((m) =>
      translate(
        m.from === "block"
          ? m.to === "confirm"
            ? "settingsControls.egress.loosen.blockToConfirm"
            : "settingsControls.egress.loosen.blockToAllow"
          : "settingsControls.egress.loosen.confirmToAllow",
        { files: translate(`settingsControls.egress.files.${m.audience}`) },
      ),
    )
    .join(" ");
}

/**
 * The confirmation's buttons for a single audience loosened: the action it moves to ("Allow",
 * "Ask first") and the one it keeps ("Keep blocked"); null for several (a reset), which then
 * reads the row's own words.
 */
export function egressConfirmWords(
  from: unknown,
  to: unknown,
): { confirm: string; keep: string } | null {
  const moves = loosenedMoves(from, to);
  const only = moves?.length === 1 ? moves[0] : undefined;
  if (!only || only.to === "block" || only.from === "allow") {
    return null;
  }
  return {
    confirm: translate(`settingsControls.egress.to.${only.to}`),
    keep: translate(`settingsControls.egress.keep.${only.from}`),
  };
}
